import { label } from "aws-amplify";
import { placeholderValues } from "../../TicketProcessingView/DataInput/TicketProcessingInput/constants";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import styles from "./AddQuarryDrawer.module.scss";
import {
  AddQuarryParams,
  OpeningHour,
} from "../../../Types/quarryDatabaseTypes";
import { stripCurrencyFormatting } from "../../../Utilities/regex";

interface DaytimeFieldsParams {
  daySwitchVals: { [key: string]: boolean };
  setDaySwitchValue: (day: string, value: boolean) => void;
}

interface FormValues {
  quarryName: string;
  quarryId: string;
  phone: string;
  addressField: string;
  accountTerms: string;
  accountLimit: string;
  notes: string;
  addressData: {
    lat: number;
    lng: number;
    address: string;
  };
  addressLat: number;
  addressLng: number;
  mondayStartTime: string;
  mondayEndTime: string;
  tuesdayStartTime: string;
  tuesdayEndTime: string;
  wednesdayStartTime: string;
  wednesdayEndTime: string;
  thursdayStartTime: string;
  thursdayEndTime: string;
  fridayStartTime: string;
  fridayEndTime: string;
  saturdayStartTime: string;
  saturdayEndTime: string;
  sundayStartTime: string;
  sundayEndTime: string;
  accountTermsCustom: string;
}

export interface DaySwitchVals {
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
}

export const DrawerConstants = {
  WIDTH: "50rem",
  ADD_QUARRY_LABEL: "Add Quarry",
  EDIT_QUARRY_LABEL: "Edit Quarry",
  COORDINATES_LABEL: "Coordinates",
  SUBMIT_AND_VIEW_LABEL: "Submit and Open Profile",
  CANCEL_LABEL: "Cancel",
  SUBMIT_LABEL: "Submit",
};

export const getFormFields = () => [
  {
    name: "quarryName",
    label: <span className={styles.formLabels}>Quarry Name</span>,
    placeholder: "Enter quarry name",
    type: InputfieldTypes.TEXT,
  },
  {
    name: "quarryId",
    label: <span>Quarry ID</span>,
    placeholder: "Enter quarry ID",
    type: InputfieldTypes.TEXT,
    disabled: true,
  },
  {
    name: "phone",
    label: <span className={styles.formLabels}>Quarry Phone Number</span>,
    placeholder: "Enter phone number",
    type: InputfieldTypes.ONCHANGE_TEXT,
  },
  {
    name: "addressField",
    label: <span className={styles.formLabels}>Location</span>,
    placeholder: "Search address",
    type: InputfieldTypes.GOOGLE,
  },
  {
    name: "addressLat",
    label: <span className={styles.formLabels}>Latitude</span>,
    placeholder: "Enter latitude",
    type: InputfieldTypes.TEXT,
  },
  {
    name: "addressLng",
    label: <span className={styles.formLabels}>Longitude</span>,
    placeholder: "Enter longitude",
    type: InputfieldTypes.TEXT,
  },
  {
    name: "accountTerms",
    label: <span className={styles.formLabels}>Account Terms</span>,
    placeholder: "Select",
    type: InputfieldTypes.SELECT,
    showSearch: true,
  },
  {
    name: "accountTermsCustom",
    label: <span className={styles.formLabels}>Custom Account Terms</span>,
    placeholder: "Enter Account Terms",
    type: InputfieldTypes.TEXT,
    prefix: "Net ",
  },
  {
    name: "accountLimit",
    label: <span className={styles.formLabels}>Account Charge Limit</span>,
    placeholder: "Enter charge limit",
    type: InputfieldTypes.TEXT,
  },
];

export const getDayTmeFields = (params?: DaytimeFieldsParams) => {
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const fields: { [key: string]: any[] } = {};

  days.forEach((day) => {
    fields[day] = [
      params?.daySwitchVals[day],
      {
        type: InputfieldTypes.SWITCH,
        name: `${day}Switch`,
        label: day.charAt(0).toUpperCase() + day.slice(1),
        onChange: (value: boolean) => params?.setDaySwitchValue(day, value),
      },
      {
        type: InputfieldTypes.TIME_PICKER,
        name: `${day}StartTime`,
        placeholder: "Select",
      },
      {
        type: InputfieldTypes.TIME_PICKER,
        name: `${day}EndTime`,
        placeholder: "Select",
      },
    ];
  });

  return fields;
};

export const createQuarryPayload = (
  formValues: FormValues,
  daySwitchVals: DaySwitchVals,
  disableCoordinates: boolean
): AddQuarryParams => {
  const openingHours: OpeningHour[] = [];
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  for (const day of days) {
    if (daySwitchVals[day as keyof DaySwitchVals]) {
      openingHours.push({
        day: day.charAt(0).toUpperCase() + day.slice(1),
        open: formValues[`${day}StartTime` as keyof FormValues] as string,
        close: formValues[`${day}EndTime` as keyof FormValues] as string,
      });
    } else {
      openingHours.push({
        day: day.charAt(0).toUpperCase() + day.slice(1),
        closed: true,
      });
    }
  }

  return {
    quarryName: formValues.quarryName,
    quarryId: formValues.quarryId,
    phone: formValues.phone,
    ...(disableCoordinates
      ? {
          enableCoordinates: true,
          lat: formValues.addressLat,
          lng: formValues.addressLng,
        }
      : {
          address: formValues.addressField,
          lat: formValues.addressData.lat,
          lng: formValues.addressData.lng,
        }),
    accountTerms:
      formValues.accountTerms === "Custom Input"
        ? formValues.accountTermsCustom
        : formValues.accountTerms,
    accountLimit: formValues.accountLimit?.trim(),
    notes: formValues.notes,
    openingHours,
  };
};

export const DrawerMessages = {
  QUARRY_ADDED_SUCCESS: "Quarry added successfully!",
  QUARRY_UPDATED_SUCCESS: "Quarry updated successfully!",
  TIMES_COPIED_SUCCESS: "Times copied to all other open days!",
  QUARRY_ADD_FAILED: "Failed to add quarry!",
  SET_TIMES_FIRST_WARNING: (day: string) =>
    `Please set ${day}'s start and end time first.`,
  COPY_PASTE_TOOLTIP: "All the same",
  ERROR_MESSAGES: [
    "Quarry Id already exists",
    "Quarry name already exists",
    "Latitude must be a valid number",
    "Longitude must be a valid number",
    "Latitude must be a valid number. Longitude must be a valid number",
  ],
};

export const getExistingDays = (formValues) => {
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const result = {};

  days.forEach((day) => {
    const start = formValues[`${day}StartTime`];
    const end = formValues[`${day}EndTime`];
    result[day] = Boolean(start || end);
  });

  return result;
};
